import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import PageHeader from "@/components/UI/PageHeader";
import { useScopeParams } from "@/hooks/useScopeParams";
import { PortfolioMetricTile } from "@/views/Portfolio/shared";
import {
  fetchTrialDocuments,
  groupSections,
  MetricsCard,
  StagedChecklist,
} from "@/components/Documents/tmf";

const tooltipText =
  "TMF checklist for this trial's ISF workspace, organized by trial stage " +
  "and artifact section (the numbered document taxonomy): expected " +
  "placeholders vs collected content, rolled up across binders. Completed " +
  "stages remain on file and fully visible — only the collection focus " +
  "moves. Expand a section to see its " +
  "documents and which binder holds each. Subject files are grouped " +
  "separately — they are per-participant ISF records, not regulatory " +
  "artifacts. Binder names in this dataset are vendor-anonymized " +
  "placeholders; real deployments show the source system's binder names.";

export default function Documents() {
  const { id: trialId } = useScopeParams();

  const { data, isLoading } = useQuery({
    queryKey: ["trial-documents-checklist", trialId],
    queryFn: () => fetchTrialDocuments(trialId!),
    enabled: !!trialId,
    staleTime: 60_000,
  });

  const now = Date.now();
  const rows = useMemo(() => data?.results ?? [], [data]);

  const sections = useMemo(() => groupSections(rows, now), [rows, now]);

  const kpis = useMemo(() => {
    const expected = data?.count ?? rows.length;
    const collected = rows.filter((r) => r.document_type === "content").length;
    return {
      expected,
      collected,
      completeness: expected
        ? `${((100 * collected) / expected).toFixed(1)}%`
        : "—",
      openQueries: rows.reduce((n, r) => n + (r.open_reviews ?? 0), 0),
      sections: sections.filter((s) => !s.isSubjectFiles).length,
    };
  }, [rows, data, sections]);

  return (
    <div className="w-full p-4">
      <div className="flex justify-between mb-4">
        <PageHeader
          title="Documents: TMF Checklist"
          tooltipText={tooltipText}
        />
      </div>
      <div className="mb-4">
        <MetricsCard columns={5}>
          <PortfolioMetricTile label="Expected" value={kpis.expected} />
          <PortfolioMetricTile label="Collected" value={kpis.collected} />
          <PortfolioMetricTile label="Completeness" value={kpis.completeness} />
          <PortfolioMetricTile
            label="Open monitor queries"
            value={kpis.openQueries}
          />
          <PortfolioMetricTile
            label="Artifact sections"
            value={kpis.sections}
          />
        </MetricsCard>
      </div>
      <StagedChecklist sections={sections} loading={isLoading} />
    </div>
  );
}
